import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";

// Terminal-style output: a dark, monospaced, auto-scrolling log of tool output. The panel is
// dark in both themes (it reads as a terminal), so its colours come from the fixed slate scale.
const consoleLineVariants = cva("min-h-[1.25em] whitespace-pre-wrap break-words", {
  variants: {
    tone: {
      plain: "text-slate-200",
      muted: "text-slate-500",
      head: "font-semibold text-sky-300",
      info: "text-cyan-300",
      success: "text-emerald-400",
      warn: "text-amber-300",
      error: "text-rose-400",
      cmd: "mt-2 font-semibold text-white before:text-emerald-400 before:content-['$_']",
    },
  },
  defaultVariants: { tone: "plain" },
});

export type ConsoleTone = NonNullable<VariantProps<typeof consoleLineVariants>["tone"]>;
export interface ConsoleLine { id: number; text: string; tone?: ConsoleTone | undefined }

export interface ConsoleOutputProps extends React.HTMLAttributes<HTMLDivElement> {
  lines: ConsoleLine[];
  /** Shown when there are no lines yet. */
  placeholder?: React.ReactNode;
  /** Rendered under the last line, e.g. a running progress bar. */
  footer?: React.ReactNode;
}

const ConsoleOutput = React.forwardRef<HTMLDivElement, ConsoleOutputProps>(
  ({ lines, placeholder, footer, className, ...props }, ref) => {
    const box = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => box.current!);
    // Follow new output, the way a terminal does (scrolls the panel only, never the page).
    React.useEffect(() => {
      const el = box.current;
      if (el) el.scrollTop = el.scrollHeight;
    }, [lines.length, footer]);
    return (
      <div
        ref={box}
        role="log"
        aria-live="polite"
        className={cn(
          "overflow-auto rounded-lg border border-slate-800 bg-slate-950 p-3 font-mono text-[12px] leading-5 shadow-inner",
          className,
        )}
        {...props}
      >
        {lines.length === 0 && placeholder && <div className="text-slate-500">{placeholder}</div>}
        {lines.map((l) => <div key={l.id} className={consoleLineVariants({ tone: l.tone })}>{l.text}</div>)}
        {footer}
      </div>
    );
  },
);
ConsoleOutput.displayName = "ConsoleOutput";

export { ConsoleOutput, consoleLineVariants };
